import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Play, Pause, Globe, AudioWaveform, Headphones, Facebook, Music2, Youtube,
  Radio, Podcast, Phone, Tv, ChevronDown, Disc3, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { MiniPlayer } from "@/components/MiniPlayer";
import { Equalizer } from "@/components/Equalizer";
import { SiteFooter } from "@/components/SiteFooter";

const EXAMPLES = [
  { business: "Chicago Roofing Company", genre: "Roofing · Chicago", src: "/audio/sample-chicago-roofing.mp3" },
  { business: "Superior Service", genre: "Heating & Air · Georgia", src: "/audio/sample-superior-service.mp3" },
  { business: "Bill Howe Plumbing", genre: "Plumbing · San Diego", src: "/audio/sample-bill-howe.mp3" },
];

const STEPS = [
  {
    icon: Globe,
    title: "Paste In Your Website",
    copy: "That's it. No creative brief. No songwriting. No giant questionnaire. We use the information already on your website to understand your business.",
  },
  {
    icon: AudioWaveform,
    title: "We Turn Your Business Into A Song",
    copy: "Our jingle engine creates musical concepts specifically for your business. Different hooks. Different styles. Different ways to make your name stick.",
  },
  {
    icon: Headphones,
    title: "Hear Your Jingle",
    copy: "Put on your headphones. Turn up the speakers. See which one gets stuck in your head. Your preview is free to hear.",
  },
];

const USE_CASES = [
  { icon: Facebook, label: "Facebook & Instagram" },
  { icon: Music2, label: "TikTok & Reels" },
  { icon: Youtube, label: "YouTube" },
  { icon: Radio, label: "Radio" },
  { icon: Globe, label: "Website" },
  { icon: Podcast, label: "Podcasts" },
  { icon: Phone, label: "Phone System" },
  { icon: Tv, label: "Local TV" },
];

const FAQS = [
  { q: "Is it really free to create my previews?", a: "Yes. You can create and listen to your initial jingle previews without entering a credit card." },
  { q: "What do I need to provide?", a: "Just your website address. We use the information on your site to understand your business and create your jingles." },
  { q: "How quickly will I hear them?", a: "Most previews are generated in about a minute. Generation time may vary slightly." },
  { q: "Do I need to write lyrics?", a: "No. We handle the lyrics, hook, musical direction, and production." },
  { q: "Are these generic templates?", a: "No. Your previews are created specifically around the information from your business and website." },
  { q: "What if I don't like them?", a: "Nothing happens. Listen to your previews and decide what you think. There's no obligation to continue." },
];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const CtaButton = ({ children = "CREATE MY FREE JINGLE", testId, className = "" }) => (
  <a
    href="#create"
    data-testid={testId}
    className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-8 py-4 rounded-xl shadow-[0_0_30px_rgba(255,183,3,0.3)] hover:scale-[1.03] active:scale-[0.98] transition-transform ${className}`}
  >
    {children}
  </a>
);

// Stylized sample player — the hero's audio visual
const HeroPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      document.querySelectorAll("audio").forEach((a) => a.pause());
      el.play();
    }
  };

  return (
    <div data-testid="hero-player" className="glass-panel rounded-3xl p-7 sm:p-9 relative overflow-hidden">
      <audio
        ref={audioRef}
        src="/hero-sample.mp3"
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-gold/10 blur-3xl pointer-events-none" aria-hidden />
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-6">Listen · our own jingle, made the same way yours will be</p>
      <div className="flex items-center gap-5 sm:gap-6">
        <div className={`relative h-24 w-24 sm:h-28 sm:w-28 rounded-full border-4 border-white/15 flex items-center justify-center shrink-0 ${playing ? "reel-spin" : ""}`}>
          {[...Array(6)].map((_, i) => (
            <span key={i} className="absolute h-full w-1 bg-white/10" style={{ transform: `rotate(${i * 30}deg)` }} />
          ))}
          <span className="h-5 w-5 rounded-full bg-gold relative z-10" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-lg sm:text-xl leading-tight">Local Business Jingle</p>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">The Jingle · 1:00</p>
          <div className="mt-3">
            <Equalizer playing={playing} color="#FFB703" className="h-5" />
          </div>
        </div>
        <button
          data-testid="hero-player-play-btn"
          onClick={toggle}
          className="h-14 w-14 shrink-0 rounded-full bg-gradient-to-br from-gold to-[#FB8500] flex items-center justify-center shadow-[0_0_28px_rgba(255,183,3,0.4)] hover:scale-105 active:scale-95 transition-transform cursor-pointer"
          aria-label={playing ? "Pause sample jingle" : "Play sample jingle"}
        >
          {playing ? <Pause className="h-6 w-6 text-black" /> : <Play className="h-6 w-6 text-black ml-0.5" />}
        </button>
      </div>
      <div className="mt-6 flex items-end gap-1 h-10" aria-hidden>
        {[...Array(28)].map((_, i) => (
          <span
            key={i}
            className="flex-1 rounded-full bg-white/10"
            style={{ height: `${18 + Math.abs(Math.sin(i * 1.7)) * 82}%`, opacity: 0.35 + Math.abs(Math.sin(i * 2.3)) * 0.4 }}
          />
        ))}
      </div>
    </div>
  );
};

export default function Landing() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [leadModal, setLeadModal] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(true);
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    const website = url.trim();
    if (!website || !website.includes(".")) {
      toast.error("Enter your website address — like YourBusiness.com");
      return;
    }
    setLeadModal(true);
  };

  const startGeneration = async (e) => {
    e.preventDefault();
    if (!leadName.trim()) {
      toast.error("Tell us your name — first name is fine.");
      return;
    }
    if (!leadEmail.includes("@") || !leadEmail.includes(".")) {
      toast.error("That email doesn't look quite right.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/jingles/generate", {
        website_url: url.trim(),
        email: leadEmail.trim(),
        name: leadName.trim(),
        phone: leadPhone.trim(),
        sms_opt_in: smsOptIn,
        origin: window.location.origin,
      });
      navigate(`/recording-studio/${data.job_id}`);
    } catch (err) {
      toast.error("The studio hiccuped — give it one more try.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-obsidian text-white relative overflow-x-hidden">
      {/* Minimal header — logo only */}
      <header className="relative z-10 mx-auto max-w-6xl px-4 sm:px-8 pt-6">
        <a href="/" data-testid="nav-logo" className="inline-flex items-center gap-2.5 group">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gold/10 border border-gold/40">
            <Disc3 className="h-5 w-5 text-gold group-hover:rotate-180 transition-transform duration-700" />
          </span>
          <span className="font-display font-extrabold tracking-tight text-sm sm:text-base">
            <span className="text-gold">Local</span>Business<span className="text-gold">Jingle</span>
            <span className="text-slate-500">.com</span>
          </span>
        </a>
      </header>

      {/* HERO */}
      <section className="relative z-10 mx-auto max-w-6xl px-4 sm:px-8 pt-8 sm:pt-20 pb-20 sm:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <p data-testid="hero-eyebrow" className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4 sm:mb-5">
              Custom jingles for local businesses
            </p>
            <h1 id="create" data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] scroll-mt-20">
              Get A Custom Jingle For Your Business In About <span className="text-gold">60 Seconds.</span>
            </h1>
            <p data-testid="hero-subhead" className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl">
              Paste in your website and we'll use the information already on your site to
              create a custom jingle preview specifically for your business.
              Listen to it free. <span className="text-slate-200">No credit card required.</span>
            </p>
            <form onSubmit={submit} data-testid="hero-form" className="mt-6 sm:mt-8 max-w-xl">
              <div className="glass-panel rounded-2xl p-2 flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="hero-url-input"
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="YourBusiness.com"
                  className="flex-1 bg-transparent px-4 py-3.5 text-base text-white placeholder:text-slate-600 focus:outline-none"
                />
                <button
                  data-testid="hero-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-7 py-3.5 rounded-xl shadow-[0_0_28px_rgba(255,183,3,0.35)] hover:scale-[1.03] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  CREATE MY FREE JINGLE
                </button>
              </div>
              <p data-testid="hero-microcopy" className="mt-3 text-sm text-slate-500">
                Free custom preview. No credit card required.
              </p>
            </form>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <HeroPlayer />
          </motion.div>
        </div>
      </section>

      {/* AUDIO EXAMPLES */}
      <section id="examples" className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <h2 data-testid="examples-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] max-w-3xl">
              Want To Hear What We Mean?
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-400">Listen to a few examples.</p>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {EXAMPLES.map((ex, i) => (
              <FadeIn key={ex.business} delay={i * 0.1}>
                <MiniPlayer business={ex.business} genre={ex.genre} src={ex.src} testId={`example-${i}`} />
              </FadeIn>
            ))}
          </div>
          <FadeIn className="mt-12 max-w-2xl">
            <p data-testid="examples-note" className="text-base sm:text-lg text-slate-300 leading-relaxed">
              These aren't templates where we just swap out the business name.
              Your jingles are created using information from your actual website — your
              business name, what you do, what makes you different, and the things
              customers should remember.
            </p>
            <CtaButton testId="examples-cta" className="mt-8" />
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <h2 data-testid="how-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Here's How It Works
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {STEPS.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.1}>
                <div data-testid={`how-step-${i}`} className="glass-panel rounded-2xl p-7 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="h-12 w-12 rounded-2xl bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                      <s.icon className="h-6 w-6 text-gold" />
                    </span>
                    <span className="font-mono text-xs text-coral tracking-[0.25em]">STEP {i + 1}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold tracking-tight mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{s.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn className="mt-10">
            <CtaButton testId="how-cta">LET ME HEAR MY JINGLE</CtaButton>
          </FadeIn>
        </div>
      </section>

      {/* WHY A JINGLE */}
      <section className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-24 sm:py-32">
          <FadeIn className="max-w-2xl">
            <h2 data-testid="why-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-10">
              Why A Jingle?
            </h2>
            <p className="font-display text-2xl sm:text-3xl font-bold leading-snug">
              Because people forget ads.
              <span className="text-gold"> They remember songs.</span>
            </p>
            <p className="mt-8 text-base sm:text-lg text-slate-400 leading-relaxed">
              Think about businesses you haven't visited in years... and yet somehow you
              can still remember their phone number, slogan, or business name because it
              was sung to you.
            </p>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">
              That's the whole idea.
            </p>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">
              Instead of creating one more ad people scroll past... give them something
              they might still be humming an hour later.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* USE CASES */}
      <section className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <h2 data-testid="usecases-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] max-w-3xl">
              Your Business Already Has A Brand.
            </h2>
            <p className="mt-4 font-display text-2xl sm:text-3xl font-bold text-gold">What If It Had A Sound?</p>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed">
              Imagine someone hearing your ad today... and humming your business name tomorrow.
            </p>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12">
            {USE_CASES.map((u, i) => (
              <FadeIn key={u.label} delay={i * 0.05}>
                <div data-testid={`usecase-${i}`} className="glass-panel rounded-2xl p-5 flex flex-col items-center gap-3 text-center h-full justify-center">
                  <u.icon className="h-6 w-6 text-neon" />
                  <span className="text-sm text-slate-300">{u.label}</span>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn className="mt-10">
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              One recognizable sound. One memorable hook.
              <span className="text-gold"> One more reason people remember your business.</span>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* STRONG FREE-PREVIEW CTA */}
      <section className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <div className="glass-panel rounded-3xl p-8 sm:p-14 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl pointer-events-none" aria-hidden />
              <h2 data-testid="cta-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.15] max-w-3xl">
                You Don't Have To Imagine What Yours Would Sound Like.
                <span className="text-gold"> You Can Hear It.</span>
              </h2>
              <p className="mt-8 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl">
                We're not asking you to describe the kind of jingle you think you want.
                We're not asking you to sit through a sales call. And we're not asking you
                to buy anything just to find out what we can create.
              </p>
              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
                Just give us your website. We'll make the previews. You listen. That's it.
              </p>
              <CtaButton testId="mid-cta" className="mt-10" />
              <p className="mt-3 text-sm text-slate-500">No credit card required.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <h2 data-testid="faq-heading" className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12">
              Questions, Answered.
            </h2>
          </FadeIn>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <FadeIn key={i} delay={i * 0.04}>
                  <div className="glass-panel rounded-2xl overflow-hidden">
                    <button
                      data-testid={`faq-question-${i}`}
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    >
                      <span className="font-display font-bold text-base sm:text-lg">{f.q}</span>
                      <ChevronDown className={`h-5 w-5 text-gold shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <p data-testid={`faq-answer-${i}`} className="px-6 pb-6 text-sm sm:text-base text-slate-400 leading-relaxed">
                        {f.a}
                      </p>
                    )}
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 border-t border-white/5">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 py-24 sm:py-32 text-center">
          <FadeIn>
            <h2 data-testid="final-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
              Your Business Has A Name.
              <br />
              <span className="text-gold">Let's Make It Harder To Forget.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl mx-auto leading-relaxed">
              Paste in your website and hear what your business sounds like as a jingle.
            </p>
            <CtaButton testId="final-cta" className="mt-10" />
            <p className="mt-3 text-sm text-slate-500">Free preview. No credit card required.</p>
          </FadeIn>
        </div>
      </section>

      {/* Lead capture modal — fires after URL is entered, before generation starts.
          Mobile: no auto-focus (stops iOS keyboard+zoom on open), 16px inputs (stops tap-to-zoom) */}
      <Dialog open={leadModal} onOpenChange={setLeadModal}>
        <DialogContent
          data-testid="lead-modal"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="bg-ink border-white/15 text-slate-200 rounded-2xl w-[calc(100vw-2rem)] max-w-md max-h-[88svh] overflow-y-auto p-5 sm:p-6"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl sm:text-2xl">Where should we send your finished jingle?</DialogTitle>
            <DialogDescription className="text-slate-400">
              Your previews start rendering the second you hit the button.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={startGeneration} className="space-y-3">
            <input
              data-testid="lead-name-input"
              type="text"
              autoComplete="name"
              value={leadName}
              onChange={(e) => setLeadName(e.target.value)}
              placeholder="Name"
              className="w-full rounded-xl border border-white/15 bg-obsidian/70 px-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-gold"
            />
            <input
              data-testid="lead-email-input"
              type="email"
              autoComplete="email"
              value={leadEmail}
              onChange={(e) => setLeadEmail(e.target.value)}
              placeholder="Email"
              className="w-full rounded-xl border border-white/15 bg-obsidian/70 px-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-gold"
            />
            <input
              data-testid="lead-phone-input"
              type="tel"
              autoComplete="tel"
              value={leadPhone}
              onChange={(e) => setLeadPhone(e.target.value)}
              placeholder="Phone (optional)"
              className="w-full rounded-xl border border-white/15 bg-obsidian/70 px-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-gold"
            />
            <input
              data-testid="lead-website-input"
              type="text"
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Website"
              className="w-full rounded-xl border border-white/15 bg-obsidian/70 px-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-gold"
            />
            <label className="flex items-start gap-2 cursor-pointer select-none pt-1">
              <input
                data-testid="lead-sms-checkbox"
                type="checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                className="mt-0.5 h-3 w-3 shrink-0 accent-[#FFB703] cursor-pointer"
              />
              <span data-testid="lead-sms-copy" className="text-[10px] leading-snug text-slate-500">
                By checking this box, I agree to receive SMS/text messages from LocalBusinessJingle.com
                about my jingle previews and my order (up to 4 msgs/month). Msg &amp; data rates may
                apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.
                See our <a href="/terms" className="underline hover:text-slate-300">Terms</a> and{" "}
                <a href="/privacy" className="underline hover:text-slate-300">Privacy Policy</a>.
              </span>
            </label>
            <button
              data-testid="lead-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-7 py-3.5 rounded-xl shadow-[0_0_28px_rgba(255,183,3,0.35)] hover:scale-[1.02] active:scale-[0.98] transition-transform cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              CREATE MY FREE JINGLE
            </button>
            <p className="text-center text-xs text-slate-500">Free custom preview. No credit card required.</p>
          </form>
        </DialogContent>
      </Dialog>

      <SiteFooter />
    </div>
  );
}
